import { Trans } from '@lingui/react/macro';

import type { AudienceType } from '@/api/audience';
import type { IntegrationProviderType } from '@/api/integration';
import { getProviderSquareLogoById } from '@/app/integrations/providerUtil';
import WarningIcon from '@/assets/svg/bxs-warning.svg?react';
import { Toggle } from '@/components/buttons';
import { Tooltip, TooltipContent, TooltipTitle, TooltipTrigger } from '@/components/Tooltip';
import { Link } from '@/components/typography';
import useControlled from '@/hooks/useControlled';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/router';

import { getActivationAudienceType, isActivationAllowed } from './audience-activation-util';

type Props = {
  id?: string;
  defaultValue?: IntegrationProviderType;
  value?: IntegrationProviderType;
  hasActiveCRMConnection?: boolean;
  audience: AudienceType;
  options: IntegrationProviderType[];
  onChange?: (value?: IntegrationProviderType) => void;
  onBlur?: () => void;
};

const AudienceActivationProviderChooser = ({
  defaultValue,
  value: valueFromProps,
  hasActiveCRMConnection = false,
  audience,
  options,
  onChange: onChangeFromProps,
  onBlur,
  ...rest
}: Props) => {
  const [value, onChange] = useControlled(valueFromProps, defaultValue, onChangeFromProps, true);

  return (
    <div
      className="m-auto grid max-w-100 grid-cols-2 justify-items-center gap-x-4 gap-y-2"
      onBlur={onBlur}
      {...rest}
    >
      {options?.map((provider) => {
        const disabled = !hasActiveCRMConnection && getActivationAudienceType(provider) === 'email';
        const isInvalid = !isActivationAllowed(audience, provider);

        if (disabled) {
          return (
            <Tooltip key={provider.id}>
              <TooltipTrigger
                render={
                  <div
                    className={cn(
                      'h-10 w-full cursor-not-allowed rounded-xs bg-gray-100 p-3 px-3.5 text-base leading-none font-semibold text-gray-400 select-none',
                      'flex items-center justify-center gap-2',
                    )}
                  >
                    <div className="size-5 opacity-60 grayscale">
                      {getProviderSquareLogoById(provider.id)}
                    </div>
                    {provider.name}
                    <WarningIcon className="size-5 text-yellow-400" />
                  </div>
                }
              />
              <TooltipContent className="max-w-sm flex-col items-start">
                <TooltipTitle>
                  <Trans>CRM Connection Required</Trans>
                </TooltipTitle>
                <Trans>
                  This audience sharing partner requires CRM contact emails in order to create a
                  custom audience within their platform. Please connect CRM to continue.{' '}
                  <Link
                    variant="body2"
                    color="static-green"
                    to={ROUTES.integrationByCategory('crm')}
                  >
                    Connect CRM
                  </Link>
                </Trans>
              </TooltipContent>
            </Tooltip>
          );
        }

        return (
          <Toggle
            key={provider.id}
            variant="ghost"
            size="large"
            className={cn('w-full', isInvalid && 'border-2 border-solid! border-red-300')}
            pressed={value?.id === provider.id}
            onPressedChange={(pressed) => onChange(pressed ? provider : undefined)}
          >
            <div className="size-5">{getProviderSquareLogoById(provider.id)}</div>
            {provider.name}
          </Toggle>
        );
      })}
    </div>
  );
};

export default AudienceActivationProviderChooser;
